import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { fileToDataUrl } from "@/lib/img";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Eye, ImagePlus, Trash2 } from "lucide-react";

const FIELDS = [
  ["subtitle", "Subtitle (custom background only)"],
  ["intro", "Line above the name (custom background only)"],
  ["completed_text", "Line above the course title"],
  ["statement", "Course statement (max 2 lines)"],
];

export function CertTemplateDialog({ course, onClose }) {
  const [tpl, setTpl] = useState(null);
  const [background, setBackground] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => { api.get(`/courses/${course.id}/certificate-template`).then(({ data }) => setTpl(data)); }, [course.id]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const payload = () => ({ ...Object.fromEntries(FIELDS.map(([k]) => [k, tpl[k] || null])), background, design: tpl.design || "" });
  const hasBg = background === null ? tpl?.has_background : !!background;

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBackground(await fileToDataUrl(f, 2000, 0.9));
  };

  const preview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/courses/${course.id}/certificate-template/preview`, payload(), { responseType: "blob" });
      setPreviewUrl(URL.createObjectURL(data));
    } catch (e) { toast.error("Could not build preview"); }
    setBusy(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/courses/${course.id}/certificate-template`, payload());
      toast.success("Certificate template saved");
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="cert-template-dialog">
        <DialogHeader><DialogTitle className="font-heading">Certificate template · {course.title}</DialogTitle></DialogHeader>
        {!tpl ? <p className="text-sm text-muted-foreground">Loading...</p> : (
          <div className="space-y-4">
            <div>
              <Label>Certificate design</Label>
              <select value={tpl.design || ""} onChange={(e) => setTpl({ ...tpl, design: e.target.value })} className="mt-1.5 w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="cert-template-design-select">
                <option value="">Automatic (Paediatric First Aid design for 12hr Blended PFA, otherwise standard)</option>
                <option value="standard">Standard CPD certificate</option>
                <option value="pfa">Paediatric First Aid certificate (name, date and trainer filled in)</option>
              </select>
            </div>
            <div className="rounded-lg border border-border p-3">
              <p className="text-sm font-medium">Background design</p>
              <p className="text-xs text-muted-foreground mt-0.5">{hasBg ? "Custom background uploaded. All wording is printed on top of it." : "Using the standard Early Years Academy certificate."} Landscape images (about 1491×1055) work best.</p>
              <div className="flex gap-2 mt-2">
                <label className="inline-flex">
                  <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={pickFile} data-testid="cert-template-background-input" />
                  <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-border cursor-pointer hover:bg-accent/40"><ImagePlus className="h-4 w-4" />Upload background</span>
                </label>
                {hasBg && <Button variant="outline" size="sm" onClick={() => setBackground("")} data-testid="cert-template-remove-background"><Trash2 className="h-4 w-4 mr-1" />Use standard design</Button>}
              </div>
            </div>
            {FIELDS.map(([k, label]) => (
              <div key={k}>
                <Label>{label}</Label>
                {k === "statement"
                  ? <Textarea value={tpl[k] || ""} onChange={(e) => setTpl({ ...tpl, [k]: e.target.value })} placeholder={tpl.defaults[k]} className="mt-1.5" rows={2} data-testid={`cert-template-${k}-input`} />
                  : <Input value={tpl[k] || ""} onChange={(e) => setTpl({ ...tpl, [k]: e.target.value })} placeholder={tpl.defaults[k]} className="mt-1.5" data-testid={`cert-template-${k}-input`} />}
              </div>
            ))}
            <p className="text-xs text-muted-foreground">Leave a box empty to keep the standard wording. The candidate's name, course title and date completed are added automatically.</p>
            {previewUrl && <iframe src={`${previewUrl}#toolbar=0&view=FitH`} title="Certificate preview" className="w-full border border-border rounded-lg" style={{ height: 420 }} data-testid="cert-template-preview" />}
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={preview} disabled={!tpl || busy} data-testid="cert-template-preview-button"><Eye className="h-4 w-4 mr-1.5" />Preview</Button>
          <Button onClick={save} disabled={!tpl || busy} data-testid="cert-template-save-button">Save template</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
